import { SessionStore } from '@backend/auth'
import { SessionInfo } from '@core/types'
import { CustomerData, Reservation } from '@backend/domain'
import { ReservationRepository, SettingsRepository } from '@backend/repository'
import { ReservationState } from '@core/types'
import {
	ConflictException,
	Injectable,
	NotFoundException,
	UnprocessableEntityException,
} from '@nestjs/common'
import { isAfter, isBefore } from 'date-fns'
import { Transactional } from 'typeorm-transactional'
import { SettingsMapper } from '../../settings'
import { ReservationUtilsService, TableAssignmentService } from '../shared'
import {
	ManagedReservationLockReason,
	ManagedReservationResponse,
	ReservationEmailEvent,
	TimeSlot,
	UpdateManagedReservationRequest,
} from '../types'
import { TimeslotService } from './timeslot.service'
import { normalizeCompanionEmails } from '../utils/companion-emails.util'

export const MANAGEMENT_ERRORS = {
	INVALID_LINK: 'INVALID_MANAGEMENT_LINK',
	ALREADY_CANCELLED: 'RESERVATION_ALREADY_CANCELLED',
	IN_PAST: 'RESERVATION_IN_PAST',
	NOT_MODIFIABLE: 'RESERVATION_NOT_MODIFIABLE',
	SLOT_UNAVAILABLE: 'SLOT_UNAVAILABLE',
	INVALID_DATES: 'INVALID_DATES',
} as const

const MODIFIABLE_STATES = new Set([ReservationState.unconfirmed, ReservationState.approved])

/**
 * Guest self-service on a single reservation, authorized exclusively by the
 * reservation's management token (no account, no tenant header). The tenant
 * context is derived from the reservation itself and pushed into the
 * SessionStore so all downstream repositories stay correctly tenant-scoped.
 */
@Injectable()
export class ReservationManagementService {
	constructor(
		private readonly _timeslotService: TimeslotService,
		private readonly _settingsRepository: SettingsRepository,
		private readonly _reservationRepository: ReservationRepository,
		private readonly _tableAssignmentService: TableAssignmentService,
		private readonly _reservationUtilsService: ReservationUtilsService,
	) {}

	async getByToken(token: string): Promise<ManagedReservationResponse> {
		const reservation = await this.findByToken(token)
		return this.withTenant(reservation, () => this.toResponse(reservation))
	}

	/**
	 * Time slots for the guest's rebooking UI. The guest's own reservation is
	 * excluded from occupancy so its current slot correctly shows as available.
	 */
	async getTimeSlotsByToken(token: string, date: Date, timezone?: string, partySize?: number): Promise<TimeSlot[]> {
		const reservation = await this.findByToken(token)
		return this.withTenant(reservation, () =>
			this._timeslotService.getTimeSlotsByDate(date, timezone, {
				partySize: partySize ?? reservation.noOfPersons,
				excludeReservationId: reservation.id,
			}),
		)
	}

	@Transactional()
	async updateByToken(token: string, request: UpdateManagedReservationRequest): Promise<ManagedReservationResponse> {
		const existing = await this.findByToken(token)
		return this.withTenant(existing, () => this.performUpdate(existing, request))
	}

	private async performUpdate(
		existing: Reservation,
		request: UpdateManagedReservationRequest,
	): Promise<ManagedReservationResponse> {
		this.assertModifiable(existing)

		const startDate = new Date(request.startDate)
		const endDate = new Date(request.endDate)

		if (isBefore(startDate, new Date())) {
			throw new UnprocessableEntityException(MANAGEMENT_ERRORS.IN_PAST)
		}
		if (isAfter(startDate, endDate)) {
			throw new UnprocessableEntityException(MANAGEMENT_ERRORS.INVALID_DATES)
		}

		// Serialize with other bookings of this tenant, then re-check capacity for
		// the requested slot while ignoring the reservation being moved itself.
		if (existing.tenantId) await this._reservationRepository.acquireBookingLock(existing.tenantId)

		const { tables, combination: tableCombination } = await this._tableAssignmentService.assignTable(
			{
				noOfPersons: request.noOfPersons,
				startDate,
				endDate,
			},
			{ excludeReservationId: existing.id },
		)

		// A confirmed reservation must never be moved to a slot without capacity
		if (existing.state === ReservationState.approved && tables.length === 0) {
			throw new UnprocessableEntityException(MANAGEMENT_ERRORS.SLOT_UNAVAILABLE)
		}

		const customerData = this.mergeCustomerData(existing.customerData, request)

		await this._reservationRepository.save({
			id: existing.id,
			noOfPersons: request.noOfPersons,
			startDate,
			endDate,
			comment: request.comment,
			customerData,
			// undefined = leave unchanged; an explicit array (even empty) replaces the list
			companionEmails:
				request.companionEmails === undefined
					? existing.companionEmails
					: normalizeCompanionEmails(request.companionEmails, customerData.email),
			tables,
			tableCombination: tableCombination ?? null,
		} as Partial<Reservation>)

		const updated = await this._reservationRepository.findOne({
			where: { id: existing.id },
			relations: { tables: true, tableCombination: true },
		})
		if (!updated) throw new NotFoundException(MANAGEMENT_ERRORS.INVALID_LINK)

		await this._reservationUtilsService.notifyReservationUpdate({
			updatedReservation: updated,
			oldReservation: existing,
			event: ReservationEmailEvent.updated,
		})

		return this.toResponse(updated)
	}

	@Transactional()
	async cancelByToken(token: string): Promise<ManagedReservationResponse> {
		const existing = await this.findByToken(token)
		return this.withTenant(existing, () => this.performCancel(existing))
	}

	private async performCancel(existing: Reservation): Promise<ManagedReservationResponse> {
		if (existing.state === ReservationState.rejected) {
			throw new ConflictException(MANAGEMENT_ERRORS.ALREADY_CANCELLED)
		}
		this.assertModifiable(existing)

		await this._reservationRepository.update({ id: existing.id }, { state: ReservationState.rejected })

		const oldState = existing.state
		existing.state = ReservationState.rejected

		await this._reservationUtilsService.notifyReservationUpdate({
			updatedReservation: existing,
			updates: [{ field: 'state', oldValue: oldState, newValue: ReservationState.rejected }],
			event: ReservationEmailEvent.cancelledByGuest,
		})

		return this.toResponse(existing)
	}

	/**
	 * Runs `fn` with the reservation's tenant as session context. `enterWith`
	 * inside an awaited call would NOT reach the caller's async frame, so all
	 * tenant-scoped repository work after the token lookup must go through here.
	 */
	private withTenant<T>(reservation: Reservation, fn: () => Promise<T>): Promise<T> {
		if (!reservation.tenantId) return fn()
		return SessionStore.runWith({ tenantId: reservation.tenantId } as SessionInfo, fn)
	}

	/**
	 * Resolves the reservation for a management token. Deliberately runs without
	 * tenant scope — the token is globally unique.
	 */
	private async findByToken(token: string): Promise<Reservation> {
		if (!token || token.length < 20 || token.length > 64) {
			throw new NotFoundException(MANAGEMENT_ERRORS.INVALID_LINK)
		}

		const reservation = await this._reservationRepository.findOne({
			where: { managementToken: token },
			relations: { tables: true, tableCombination: true },
		})
		if (!reservation) {
			throw new NotFoundException(MANAGEMENT_ERRORS.INVALID_LINK)
		}

		return reservation
	}

	private assertModifiable(reservation: Reservation): void {
		if (reservation.state === ReservationState.rejected) {
			throw new ConflictException(MANAGEMENT_ERRORS.ALREADY_CANCELLED)
		}
		if (!MODIFIABLE_STATES.has(reservation.state)) {
			throw new UnprocessableEntityException(MANAGEMENT_ERRORS.NOT_MODIFIABLE)
		}
		if (isBefore(reservation.startDate, new Date())) {
			throw new UnprocessableEntityException(MANAGEMENT_ERRORS.IN_PAST)
		}
	}

	private mergeCustomerData(existing: CustomerData, request: UpdateManagedReservationRequest): CustomerData {
		const firstName = request.customerData.firstName?.trim() || undefined
		const lastName = request.customerData.lastName?.trim()

		return {
			...existing,
			firstName,
			lastName,
			name: [firstName, lastName].filter(Boolean).join(' ') || existing.name,
			email: request.customerData.email ?? existing.email,
			phoneNumber: request.customerData.phoneNumber ?? existing.phoneNumber,
		}
	}

	private async toResponse(reservation: Reservation): Promise<ManagedReservationResponse> {
		const settings = await this._settingsRepository.findOne({})
		if (!settings) throw new NotFoundException(MANAGEMENT_ERRORS.INVALID_LINK)

		const lockReason = this.lockReason(reservation)

		return {
			reservation: {
				noOfPersons: reservation.noOfPersons,
				startDate: reservation.startDate,
				endDate: reservation.endDate,
				state: reservation.state,
				comment: reservation.comment,
				name: reservation.customerData.name,
				firstName: reservation.customerData.firstName,
				lastName: reservation.customerData.lastName,
				email: reservation.customerData.email,
				phoneNumber: reservation.customerData.phoneNumber,
				companionEmails: reservation.companionEmails ?? undefined,
			},
			settings: SettingsMapper.entityToWidgetSettings(settings),
			canModify: lockReason === undefined,
			lockReason,
		}
	}

	private lockReason(reservation: Reservation): ManagedReservationLockReason | undefined {
		if (reservation.state === ReservationState.rejected) return ManagedReservationLockReason.cancelled
		if (!MODIFIABLE_STATES.has(reservation.state)) return ManagedReservationLockReason.notModifiable
		if (isBefore(reservation.startDate, new Date())) return ManagedReservationLockReason.inPast
		return undefined
	}
}
